import { useMemo, useRef } from "react"
import Fuse from "fuse.js"

/**
 * Reusable weighted Fuse.js search hook.
 *
 * @param {Object} params
 * @param {Object[]} params.items - Source array to search.
 * @param {string} params.query - User-entered search string.
 * @param {Array<string|{name: string, weight: number}>} params.keys - Fuse keys configuration.
 * @param {number} [params.minQueryLength=2] - Minimum trimmed query length required before fuzzy searching.
 * @param {Object} [params.fuseOptions={}] - Additional Fuse options to merge.
 * @param {number} [params.maxMatchScore] - Optional maximum Fuse score to keep (lower score means better match).
 * @param {number} [params.minQueryLengthForScoreFilter=4] - Minimum query length before score cutoff is applied.
 * @param {boolean} [params.fallbackToUnfilteredOnEmpty=true] - If cutoff removes all results, return unfiltered Fuse results instead.
 * @returns {Object[]} Filtered items; returns original items when query is shorter than minQueryLength.
 */
const useWeightedFuseSearch = ({
  items,
  query,
  keys,
  minQueryLength = 2,
  fuseOptions = {},
  maxMatchScore,
  minQueryLengthForScoreFilter = 4,
  fallbackToUnfilteredOnEmpty = true,
}) => {
  const fuseOptionsRef = useRef(fuseOptions)
  fuseOptionsRef.current = fuseOptions
  const fuseOptionsKey = JSON.stringify(fuseOptions)

  const fuse = useMemo(() => {
    return new Fuse(items, {
      keys,
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: minQueryLength,
      ...fuseOptionsRef.current,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, keys, minQueryLength, fuseOptionsKey])

  const searchedItems = useMemo(() => {
    const normalizedQuery = (query || "").trim()

    if (normalizedQuery.length < minQueryLength) {
      return items
    }

    const results = fuse.search(normalizedQuery)

    const shouldApplyScoreFilter =
      typeof maxMatchScore === "number"
      && normalizedQuery.length >= minQueryLengthForScoreFilter

    if (!shouldApplyScoreFilter) {
      return results.map((result) => result.item)
    }

    const filteredResults = results.filter((result) => {
      if (typeof result.score !== "number") {
        return true
      }

      return result.score <= maxMatchScore
    })

    if (filteredResults.length === 0 && fallbackToUnfilteredOnEmpty) {
      return results.map((result) => result.item)
    }

    return filteredResults.map((result) => result.item)
  }, [
    items,
    query,
    minQueryLength,
    fuse,
    maxMatchScore,
    minQueryLengthForScoreFilter,
    fallbackToUnfilteredOnEmpty,
  ])

  return searchedItems
}

export default useWeightedFuseSearch
